import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Mail, Clock, Heart, ChevronDown } from 'lucide-react';
import { siteConfig } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';
import { SEO, pageSEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  {
    question: "What kind of work is The Soul Script?",
    answer: "A reflective practice focused on emotional resilience, self-inquiry, and conscious living."
  },
  {
    question: "Is tarot used to predict the future?",
    answer: "No. Tarot is used as a reflective tool for insight and perspective, not prediction."
  },
  {
    question: "Are sessions a substitute for therapy?",
    answer: "No. Sessions are not a replacement for medical, psychological, or legal care."
  },
  {
    question: "What currency are prices listed in?",
    answer: "All prices on the website are listed in Euros (EUR)."
  },
  {
    question: "How are payments handled?",
    answer: "Payments are processed securely through trusted third-party platforms."
  },
  {
    question: "Do you offer refunds?",
    answer: "Refunds are not offered once a session is booked, but rescheduling is available with 24 hours notice to the earliest available spot."
  },
  {
    question: "How should I prepare for a session?",
    answer: "You may be invited to reflect or journal beforehand, but no preparation is required."
  }
];

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-[var(--color-dark-purple)]/30 last:border-b-0">
      <button
        onClick={onClick}
        className="w-full py-6 flex items-center justify-between text-left group"
        data-testid={`faq-${question.toLowerCase().replace(/\s+/g, '-').slice(0, 30)}`}
      >
        <span className="font-serif text-lg text-[var(--color-cream)] group-hover:text-[var(--color-gold)] transition-colors pr-4">
          {question}
        </span>
        <ChevronDown 
          className={`w-5 h-5 text-[var(--color-gold)] flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="pb-6 text-[var(--color-cream)]/60 leading-relaxed">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      
      toast.success('Message sent successfully', {
        description: 'I\'ll respond within 2-3 business days.'
      });
      
      // Redirect to thank you page
      navigate('/thank-you?type=contact');
      
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message', {
        description: 'Please try again or email directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[var(--color-charcoal)]">
      <SEO {...pageSEO.contact} path="/contact" />
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center pt-32 pb-20">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-dark-purple)]/20 to-[var(--color-charcoal)]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="max-w-3xl"
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-6 block">
              Get in Touch
            </span>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mb-8 leading-[1.05]">
              Contact
            </h1>
            <p className="text-xl text-[var(--color-cream)]/70 leading-relaxed">
              Questions, inquiries, or simply something on your heart. This is a space for respectful connection.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* Form */}
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="font-serif text-3xl mb-8">Send a Message</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm text-[var(--color-cream)]/70 mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-6 py-4 bg-[var(--color-charcoal-light)] border border-[var(--color-dark-purple)]/50 rounded-lg text-[var(--color-cream)] placeholder:text-[var(--color-cream)]/40 focus:outline-none focus:border-[var(--color-gold)]/50 transition-colors"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm text-[var(--color-cream)]/70 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-6 py-4 bg-[var(--color-charcoal-light)] border border-[var(--color-dark-purple)]/50 rounded-lg text-[var(--color-cream)] placeholder:text-[var(--color-cream)]/40 focus:outline-none focus:border-[var(--color-gold)]/50 transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="subject" className="block text-sm text-[var(--color-cream)]/70 mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-6 py-4 bg-[var(--color-charcoal-light)] border border-[var(--color-dark-purple)]/50 rounded-lg text-[var(--color-cream)] placeholder:text-[var(--color-cream)]/40 focus:outline-none focus:border-[var(--color-gold)]/50 transition-colors"
                      placeholder="What is this regarding?"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm text-[var(--color-cream)]/70 mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      minLength={10}
                      rows={6}
                      className="w-full px-6 py-4 bg-[var(--color-charcoal-light)] border border-[var(--color-dark-purple)]/50 rounded-lg text-[var(--color-cream)] placeholder:text-[var(--color-cream)]/40 focus:outline-none focus:border-[var(--color-gold)]/50 transition-colors resize-none"
                      placeholder="Share what's on your mind..."
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center gap-3 px-8 py-4 bg-[var(--color-gold)] text-[var(--color-charcoal)] font-medium rounded-full hover:bg-[var(--color-gold-light)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="sticky top-32 space-y-8"
              >
                {/* Direct Email */}
                <div className="p-8 bg-[var(--color-charcoal-light)] rounded-lg border border-[var(--color-dark-purple)]/30">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-[var(--color-deep-red)]/20 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-[var(--color-deep-red-light)]" />
                    </div>
                    <h3 className="font-serif text-xl">Direct Email</h3>
                  </div>
                  <p className="text-[var(--color-cream)]/60 mb-4">
                    Prefer email? Reach out directly:
                  </p>
                  <a
                    href={`mailto:${siteConfig.email}`}
                    className="text-[var(--color-gold)] hover:text-[var(--color-gold-light)] transition-colors"
                  >
                    {siteConfig.email}
                  </a>
                </div>

                {/* Response Time */}
                <div className="p-8 bg-[var(--color-charcoal-light)] rounded-lg border border-[var(--color-dark-purple)]/30">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-[var(--color-bottle-green)]/20 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-[var(--color-bottle-green-light)]" />
                    </div>
                    <h3 className="font-serif text-xl">Response Time</h3>
                  </div>
                  <p className="text-[var(--color-cream)]/60">
                    I aim to respond to all inquiries within 2-3 business days. If your matter is urgent, please indicate so in the subject line.
                  </p>
                </div>

                {/* Boundaries Note */}
                <div className="p-8 bg-[var(--color-dark-purple)]/10 rounded-lg border border-[var(--color-dark-purple)]/30">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-[var(--color-dark-purple)]/30 flex items-center justify-center">
                      <Heart className="w-5 h-5 text-[var(--color-cream)]/70" />
                    </div>
                    <h3 className="font-serif text-xl">A Note on Boundaries</h3>
                  </div>
                  <p className="text-[var(--color-cream)]/60 text-sm leading-relaxed">
                    This space holds deep respect for boundaries—yours and mine. While I welcome genuine inquiries, I cannot provide crisis support or emergency services through this channel. If you are in crisis, please reach out to appropriate mental health services in your area.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 lg:py-32 bg-[var(--color-charcoal-light)]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-4 block">
              Common Questions
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[var(--color-charcoal)] rounded-lg p-8 lg:p-10"
            data-testid="faq-section"
          >
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onClick={() => toggleFAQ(index)}
              />
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};
